import { ReactElement } from 'react';
import { Box, FormControl, InputLabel, MenuItem, Select, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import { primarySoft } from '../../../../../../theme';
import { BarcodeFormat } from './barcodeResult';

const iconSx = { fontSize: '1.1rem' };

const BARCODE_FORMATS: Array<{ value: BarcodeFormat; label: string; icon: ReactElement }> = [
  { value: 'text', label: 'Texte', icon: <TextFieldsIcon sx={iconSx} /> },
  { value: 'barcode_ean', label: 'Code-barre avec EAN', icon: <QrCode2Icon sx={iconSx} /> },
  { value: 'barcode_no_ean', label: 'Code-barre sans EAN', icon: <QrCode2Icon sx={iconSx} /> },
  { value: 'small_barcode_ean', label: 'Petit code-barre avec EAN', icon: <QrCode2Icon sx={iconSx} /> },
  { value: 'small_barcode_no_ean', label: 'Petit code-barre sans EAN', icon: <QrCode2Icon sx={iconSx} /> },
];

interface OfferCodeTabProps {
  offerCount: number;
  offerTitles?: string[];
  selectedOffer: number;
  onSelectOffer: (offerIndex: number) => void;
  format: BarcodeFormat;
  onFormatChange: (format: BarcodeFormat) => void;
}

const offerLabel = (index: number, existingCount: number, title: string | undefined) => {
  if (index >= existingCount) return `+ Nouvelle offre (Offre ${index + 1})`;
  return title?.trim() ? `Offre ${index + 1} — ${title}` : `Offre ${index + 1}`;
};

function OfferCodeTab({ offerCount, offerTitles, selectedOffer, onSelectOffer, format, onFormatChange }: OfferCodeTabProps) {
  const existingCount = Math.max(1, offerCount);

  return (
    <Box>
      <FormControl size="small" fullWidth sx={{ mb: 2.5 }}>
        <InputLabel sx={{ '&.Mui-focused': { color: 'primary.main' } }}>Numéro de l&apos;offre</InputLabel>
        <Select
          value={selectedOffer}
          label="Numéro de l'offre"
          onChange={e => onSelectOffer(Number(e.target.value))}
          sx={{ borderRadius: 1.5, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' } }}
        >
          {Array.from({ length: existingCount + 1 }, (_, index) => (
            <MenuItem key={index} value={index}>{offerLabel(index, existingCount, offerTitles?.[index])}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <Typography variant="caption" fontWeight={700} sx={{ display: 'block', mb: 1, color: 'text.secondary', fontSize: '0.7rem', letterSpacing: 0.5, textTransform: 'uppercase' }}>
        Format
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        {BARCODE_FORMATS.map(option => {
          const selected = format === option.value;
          return (
            <Box
              key={option.value}
              onClick={() => onFormatChange(option.value)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 1.5,
                py: 1,
                borderRadius: 1.5,
                border: '1.5px solid',
                borderColor: selected ? 'primary.main' : 'divider',
                backgroundColor: selected ? theme => primarySoft(theme, 0.05) : 'background.paper',
                cursor: 'pointer',
                transition: 'all 0.15s',
                '&:hover': { borderColor: 'primary.main', backgroundColor: theme => primarySoft(theme, 0.03) },
              }}
            >
              <Box sx={{ color: selected ? 'primary.main' : 'text.disabled', display: 'flex' }}>{option.icon}</Box>
              <Typography variant="body2" sx={{ fontSize: '0.82rem', fontWeight: selected ? 600 : 400, color: selected ? 'primary.main' : 'text.primary' }}>
                {option.label}
              </Typography>
              {selected && <CheckIcon sx={{ fontSize: '0.9rem', color: 'primary.main', ml: 'auto' }} />}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

export default OfferCodeTab;
